import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { uploadToCloudinary } from "./cloudinary";
import { PLATFORM_OPTIONS, PLATFORM_ICONS } from "./contactsConfig";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, getDoc, setDoc,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged,
} from "firebase/auth";
import "./Admin.css";

const EMPTY = { name: "", description: "", etsy: "", patreon: "", images: [] };

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setChecking(false);
    });
    return unsub;
  }, []);

  if (checking) return <div className="admin-loading">Loading…</div>;
  if (!user) return <Login />;
  return <Dashboard user={user} />;
}

// ── Login ──────────────────────────────────────────────────────────────────
function Login() {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pass);
    } catch {
      setErr("Wrong email or password.");
    }
    setBusy(false);
  };

  return (
    <div className="admin-login">
      <form className="admin-login__card" onSubmit={submit}>
        <h1>Sprykal Admin</h1>
        <p className="admin-login__sub">Sign in to manage your designs.</p>
        <input className="admin-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className="admin-input" type="password" placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)} required />
        {err && <p className="admin-err">{err}</p>}
        <button className="admin-btn admin-btn--dark" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <a className="admin-back" href="/">← Back to site</a>
      </form>
    </div>
  );
}

// ── Site settings (home image + page background) ──────────────────────────
const BG_PRESETS = [
  { label: "White", value: "#ffffff" },
  { label: "Cream", value: "#f7f6f3" },
  { label: "Warm grey", value: "#ece8e1" },
  { label: "Dark", value: "#1a1a1a" },
];

function HeroSettings() {
  const [heroImage, setHeroImage] = useState(null);
  const [background, setBackground] = useState("");
  const [uploading, setUploading] = useState(false);
  const [bgUploading, setBgUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "site"));
        if (snap.exists()) {
          setHeroImage(snap.data().heroImage || null);
          setBackground(snap.data().background || "");
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  const onHeroFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      await setDoc(doc(db, "settings", "site"), { heroImage: url }, { merge: true });
      setHeroImage(url);
    } catch (err) {
      alert("Upload failed. Check Cloudinary settings.");
      console.error(err);
    }
    setUploading(false);
    e.target.value = "";
  };

  const saveBackground = async (value) => {
    setBackground(value);
    try {
      await setDoc(doc(db, "settings", "site"), { background: value }, { merge: true });
    } catch (err) {
      alert("Could not save background.");
      console.error(err);
    }
  };

  const onBgFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      await saveBackground(url);
    } catch (err) {
      alert("Upload failed. Check Cloudinary settings.");
      console.error(err);
    }
    setBgUploading(false);
    e.target.value = "";
  };

  const bgIsImage = /^https?:\/\//.test(background);

  return (
    <>
      <h2 className="admin-section-title">Home page image</h2>
      <div className="hero-settings">
        <div className="hero-settings__info">
          <strong>Hero image</strong>
          <p>The big image shown on your home page.</p>
        </div>
        <div className="hero-settings__preview">
          {heroImage && <img src={heroImage} alt="Home hero" />}
        </div>
        <label className="admin-btn admin-btn--ghost">
          {uploading ? "Uploading…" : "Change image"}
          <input type="file" accept="image/*" onChange={onHeroFile} hidden disabled={uploading} />
        </label>
      </div>

      <h2 className="admin-section-title">Page background</h2>
      <div className="bg-settings">
        <p className="bg-settings__hint">Applies to all pages (Home, Collection, Contact). Pick a colour, or upload a wallpaper image.</p>
        <div className="bg-swatches">
          {BG_PRESETS.map((p) => (
            <button
              key={p.value}
              className={"bg-swatch" + (background === p.value ? " bg-swatch--active" : "")}
              style={{ background: p.value }}
              onClick={() => saveBackground(p.value)}
              title={p.label}
            >
              <span>{p.label}</span>
            </button>
          ))}
        </div>
        <div className="bg-settings__row">
          <label className="admin-btn admin-btn--ghost">
            {bgUploading ? "Uploading…" : "Upload wallpaper image"}
            <input type="file" accept="image/*" onChange={onBgFile} hidden disabled={bgUploading} />
          </label>
          {background && (
            <button className="admin-btn admin-btn--ghost" onClick={() => saveBackground("")}>
              Reset to default
            </button>
          )}
        </div>
        {bgIsImage && (
          <div className="bg-preview" style={{ backgroundImage: `url(${background})` }} />
        )}
      </div>
    </>
  );
}


// ── Contact links settings ────────────────────────────────────────────────
function ContactSettings() {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "site"));
        if (snap.exists() && Array.isArray(snap.data().contacts)) {
          setContacts(snap.data().contacts);
        }
      } catch (e) { console.error(e); }
      setLoaded(true);
    })();
  }, []);

  const update = (i, field, val) => {
    setContacts((cs) => cs.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
    setDirty(true);
  };
  const add = () => {
    setContacts((cs) => [...cs, { platform: "Email", label: "Email", desc: "", value: "" }]);
    setDirty(true);
  };
  const remove = (i) => {
    setContacts((cs) => cs.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const move = (i, dir) => {
    setContacts((cs) => {
      const next = [...cs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return cs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = contacts
        .filter((c) => c.value && c.value.trim())
        .map((c) => ({
          platform: c.platform || "Link",
          label: (c.label || c.platform || "").trim(),
          desc: (c.desc || "").trim(),
          value: c.value.trim(),
        }));
      await setDoc(doc(db, "settings", "site"), { contacts: clean }, { merge: true });
      setDirty(false);
    } catch (e) {
      alert("Could not save contact links.");
      console.error(e);
    }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <>
      <h2 className="admin-section-title">Contact links</h2>
      <div className="contacts-settings">
        <p className="bg-settings__hint">These show on your Contact page. Pick a platform, set the label, description, and the link or email.</p>

        {contacts.length === 0 && <p className="admin-muted" style={{ marginBottom: 16 }}>No contact links yet — add one below.</p>}

        {contacts.map((c, i) => (
          <div className="contact-edit" key={i}>
            <div className="contact-edit__icon">{PLATFORM_ICONS[c.platform] || PLATFORM_ICONS.Link}</div>
            <div className="contact-edit__fields">
              <div className="contact-edit__row">
                <select className="admin-input" value={c.platform} onChange={(e) => { update(i, "platform", e.target.value); if (!c.label || PLATFORM_OPTIONS.includes(c.label)) update(i, "label", e.target.value); }}>
                  {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input className="admin-input" placeholder="Label (e.g. Email)" value={c.label} onChange={(e) => update(i, "label", e.target.value)} />
              </div>
              <input className="admin-input" placeholder={c.platform === "Email" ? "you@example.com" : "link, e.g. behance.net/yourname"} value={c.value} onChange={(e) => update(i, "value", e.target.value)} />
              <input className="admin-input" placeholder="Short description" value={c.desc} onChange={(e) => update(i, "desc", e.target.value)} />
            </div>
            <div className="contact-edit__actions">
              <button className="admin-btn admin-btn--ghost contact-edit__mini" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button className="admin-btn admin-btn--ghost contact-edit__mini" onClick={() => move(i, 1)} disabled={i === contacts.length - 1}>↓</button>
              <button className="admin-btn admin-btn--danger contact-edit__mini" onClick={() => remove(i)}>✕</button>
            </div>
          </div>
        ))}

        <div className="contacts-settings__actions">
          <button className="admin-btn admin-btn--ghost" onClick={add}>+ Add link</button>
          <button className="admin-btn admin-btn--dark" onClick={save} disabled={saving || !dirty}>
            {saving ? "Saving…" : dirty ? "Save links" : "Saved"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ user }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, object = form view

  const load = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "designs"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setDesigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this design? This can't be undone.")) return;
    await deleteDoc(doc(db, "designs", id));
    load();
  };

  if (editing !== null) {
    return <DesignForm initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div className="admin">
      <header className="admin-head">
        <div>
          <h1>Your Designs</h1>
          <p className="admin-head__sub">Signed in as {user.email}</p>
        </div>
        <div className="admin-head__actions">
          <a className="admin-btn admin-btn--ghost" href="/">View site</a>
          <button className="admin-btn admin-btn--ghost" onClick={() => signOut(auth)}>Sign out</button>
          <button className="admin-btn admin-btn--dark" onClick={() => setEditing(EMPTY)}>+ Add design</button>
        </div>
      </header>

      <HeroSettings />

      <ContactSettings />

      <h2 className="admin-section-title">Designs</h2>

      {loading ? (
        <p className="admin-muted">Loading designs…</p>
      ) : designs.length === 0 ? (
        <div className="admin-empty">
          <p>No designs yet.</p>
          <button className="admin-btn admin-btn--dark" onClick={() => setEditing(EMPTY)}>Add your first design</button>
        </div>
      ) : (
        <div className="admin-list">
          {designs.map((d) => (
            <div className="admin-row" key={d.id}>
              <img className="admin-row__thumb" src={d.images?.[0]} alt={d.name} />
              <div className="admin-row__info">
                <strong>{d.name}</strong>
                <span>{d.images?.length || 0} image(s)</span>
              </div>
              <div className="admin-row__actions">
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditing(d)}>Edit</button>
                <button className="admin-btn admin-btn--danger" onClick={() => remove(d.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Add / Edit form ──────────────────────────────────────────────────────────
function DesignForm({ initial, onDone, onCancel }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    description: initial.description || "",
    etsy: initial.etsy || "",
    patreon: initial.patreon || "",
    images: initial.images || [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const url = await uploadToCloudinary(f);
        urls.push(url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
    } catch (err) {
      alert("Image upload failed. Check your Cloudinary settings.");
      console.error(err);
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return alert("Please add a name.");
    if (form.images.length === 0) return alert("Please add at least one image.");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      etsy: form.etsy.trim() || null,
      patreon: form.patreon.trim() || null,
      images: form.images,
    };
    try {
      if (isEdit) {
        await updateDoc(doc(db, "designs", initial.id), payload);
      } else {
        await addDoc(collection(db, "designs"), { ...payload, createdAt: serverTimestamp() });
      }
      onDone();
    } catch (e) {
      alert("Save failed. Check your connection / Firebase rules.");
      console.error(e);
      setSaving(false);
    }
  };

  return (
    <div className="admin">
      <header className="admin-head">
        <h1>{isEdit ? "Edit design" : "Add design"}</h1>
        <button className="admin-btn admin-btn--ghost" onClick={onCancel}>← Back</button>
      </header>

      <div className="admin-form">
        <label className="admin-label">Name</label>
        <input className="admin-input" name="name" value={form.name} onChange={handle} placeholder="e.g. Serpent Pendant" />

        <label className="admin-label">Description</label>
        <textarea className="admin-input admin-textarea" name="description" value={form.description} onChange={handle} placeholder="Short description of the piece" rows={3} />

        <label className="admin-label">Etsy link (leave empty if none)</label>
        <input className="admin-input" name="etsy" value={form.etsy} onChange={handle} placeholder="https://etsy.com/..." />

        <label className="admin-label">Patreon link (leave empty if none)</label>
        <input className="admin-input" name="patreon" value={form.patreon} onChange={handle} placeholder="https://patreon.com/..." />

        <label className="admin-label">Images (first one is the cover)</label>
        <div className="admin-images">
          {form.images.map((url, i) => (
            <div className="admin-thumb" key={i}>
              <img src={url} alt={`view ${i + 1}`} />
              <button className="admin-thumb__x" onClick={() => removeImage(i)}>✕</button>
            </div>
          ))}
          <label className="admin-upload">
            {uploading ? "Uploading…" : "+ Add"}
            <input type="file" accept="image/*" multiple onChange={onFiles} hidden disabled={uploading} />
          </label>
        </div>

        <div className="admin-form__actions">
          <button className="admin-btn admin-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="admin-btn admin-btn--dark" onClick={save} disabled={saving || uploading}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add design"}
          </button>
        </div>
      </div>
    </div>
  );
}
